import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SurpriseBagCard from './components/SurpriseBagCard';
import SearchFilterBar from './components/SearchFilterBar';
import ImpactMetrics from './components/ImpactMetrics';
import WalletBalance from './components/WalletBalance';
import QuickActions from './components/QuickActions';

const ConsumerDashboard = () => {
  const [surpriseBags, setSurpriseBags] = useState([]);
  const [filteredBags, setFilteredBags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState({});
  const [viewMode, setViewMode] = useState('grid'); // grid, list, map
  const navigate = useNavigate();

  // Mock data for surprise bags
  const mockSurpriseBags = [
  {
    id: 1,
    businessName: "Café Central",
    title: "Bolsa Sorpresa de Panadería",
    description: "Variedad de panes, pasteles y bebidas del día. Perfecto para desayuno o merienda.",
    image: "https://images.unsplash.com/photo-1626617108090-2871e1aa4b25",
    imageAlt: "Variedad de panes artesanales y pasteles frescos en canasta de mimbre sobre mesa de madera",
    originalPrice: 200,
    discountedPrice: 80,
    discountPercentage: 60,
    pickupTimeStart: "16:00",
    pickupTimeEnd: "18:00",
    walkingDistance: 5,
    urgency: "medium",
    urgencyLabel: "2h restantes",
    rating: 4.8,
    reviewCount: 124,
    itemsCount: "8-12",
    category: "bakery",
    dietaryOptions: ["vegetarian"]
  },
  {
    id: 2,
    businessName: "Panadería Luna",
    title: "Mix de Postres y Dulces",
    description: "Deliciosos postres caseros, galletas y dulces tradicionales mexicanos.",
    image: "https://images.unsplash.com/photo-1634377738242-a77b978ed9ef",
    imageAlt: "Coloridos cupcakes y postres mexicanos tradicionales decorados con glaseado en vitrina de panadería",
    originalPrice: 150,
    discountedPrice: 60,
    discountPercentage: 60,
    pickupTimeStart: "19:00",
    pickupTimeEnd: "20:30",
    walkingDistance: 8,
    urgency: "high",
    urgencyLabel: "30 min",
    rating: 4.6,
    reviewCount: 89,
    itemsCount: "6-10",
    category: "dessert",
    dietaryOptions: ["vegetarian"]
  },
  {
    id: 3,
    businessName: "Restaurante Verde",
    title: "Comida Saludable del Día",
    description: "Platos vegetarianos y veganos frescos, ensaladas y smoothies nutritivos.",
    image: "https://images.unsplash.com/photo-1695920566410-7b8aaa906487",
    imageAlt: "Platos vegetarianos coloridos con ensaladas frescas, quinoa y vegetales orgánicos en bowls de cerámica",
    originalPrice: 180,
    discountedPrice: 75,
    discountPercentage: 58,
    pickupTimeStart: "15:30",
    pickupTimeEnd: "17:00",
    walkingDistance: 12,
    urgency: "low",
    urgencyLabel: "4h restantes",
    rating: 4.9,
    reviewCount: 156,
    itemsCount: "3-5",
    category: "restaurant",
    dietaryOptions: ["vegetarian", "vegan", "gluten-free"]
  },
  {
    id: 4,
    businessName: "Supermercado Fresh",
    title: "Productos Frescos Variados",
    description: "Frutas, verduras, lácteos y productos de panadería próximos a vencer.",
    image: "https://images.unsplash.com/photo-1698150493257-6f25ba8eaebb",
    imageAlt: "Frutas y verduras frescas organizadas en cajas de madera en mercado local con iluminación natural",
    originalPrice: 120,
    discountedPrice: 50,
    discountPercentage: 58,
    pickupTimeStart: "20:00",
    pickupTimeEnd: "21:00",
    walkingDistance: 15,
    urgency: "medium",
    urgencyLabel: "1.5h restantes",
    rating: 4.4,
    reviewCount: 67,
    itemsCount: "10-15",
    category: "grocery",
    dietaryOptions: ["vegetarian", "vegan"]
  },
  {
    id: 5,
    businessName: "Taquería El Buen Sabor",
    title: "Tacos y Antojitos Mexicanos",
    description: "Tacos, quesadillas y antojitos mexicanos tradicionales recién preparados.",
    image: "https://images.unsplash.com/photo-1718949906243-0d31f03f7172",
    imageAlt: "Tacos mexicanos tradicionales con carne, cilantro y cebolla servidos en tortillas de maíz sobre plato de barro",
    originalPrice: 160,
    discountedPrice: 65,
    discountPercentage: 59,
    pickupTimeStart: "21:00",
    pickupTimeEnd: "22:30",
    walkingDistance: 7,
    urgency: "high",
    urgencyLabel: "45 min",
    rating: 4.7,
    reviewCount: 203,
    itemsCount: "4-6",
    category: "restaurant",
    dietaryOptions: []
  },
  {
    id: 6,
    businessName: "Café Aroma",
    title: "Café y Snacks de Tarde",
    description: "Café gourmet, sandwiches, muffins y snacks perfectos para estudiar.",
    image: "https://images.unsplash.com/photo-1519129176095-e300b4e24396",
    imageAlt: "Taza de café con latte art junto a muffins y sandwiches gourmet en mesa de café moderna",
    originalPrice: 140,
    discountedPrice: 55,
    discountPercentage: 61,
    pickupTimeStart: "17:00",
    pickupTimeEnd: "19:00",
    walkingDistance: 3,
    urgency: "low",
    urgencyLabel: "3h restantes",
    rating: 4.5,
    reviewCount: 91,
    itemsCount: "5-8",
    category: "cafe",
    dietaryOptions: ["vegetarian"]
  }];


  // Mock user impact data
  const mockUserImpact = {
    foodRescued: 45.2,
    waterSaved: 1250,
    co2Avoided: 18.7,
    moneySaved: 1840,
    mealsEquivalent: 38,
    showerEquivalent: 125,
    treesEquivalent: 3,
    discountPercentage: 58,
    currentStreak: 12,
    level: "Eco Warrior",
    levelProgress: 75,
    pointsToNextLevel: 250,
    recentAchievements: [
    {
      title: "Primera Semana Completa",
      description: "Rescataste comida 7 días seguidos",
      date: "hace 2 días"
    },
    {
      title: "Ahorrador de Agua",
      description: "Ahorraste más de 1000L de agua",
      date: "hace 5 días"
    }]

  };

  // Mock wallet data
  const mockWalletData = {
    balance: 485.50,
    personalBalance: 185.50,
    subsidyBalance: 300.00,
    delayCredits: 25.00,
    monthlySubsidy: 400.00,
    subsidyUsed: 75,
    nextSubsidyDate: "15 Nov",
    universityName: "Universidad Complutense Madrid",
    bagsThisMonth: 8,
    savedThisMonth: 320,
    totalSpent: 1240,
    specialOffers: [
    {
      title: "Descuento extra en Café Central",
      discount: "10% adicional"
    },
    {
      title: "Bolsa gratis por 5 compras",
      discount: "Próxima gratis"
    }]

  };

  useEffect(() => {
    // Simulate loading
    const loadData = async () => {
      setIsLoading(true);
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setSurpriseBags(mockSurpriseBags);
      setFilteredBags(mockSurpriseBags);
      setIsLoading(false);
    };

    loadData();
  }, []);

  const handleSearch = (query) => {
    setSearchQuery(query);
    filterBags(query, activeFilters);
  };

  const handleFilter = (filters) => {
    setActiveFilters(filters);
    filterBags(searchQuery, filters);
  };

  const filterBags = (query, filters) => {
    let filtered = [...mockSurpriseBags];

    // Search filter
    if (query) {
      filtered = filtered?.filter((bag) =>
      bag?.businessName?.toLowerCase()?.includes(query?.toLowerCase()) ||
      bag?.title?.toLowerCase()?.includes(query?.toLowerCase()) ||
      bag?.description?.toLowerCase()?.includes(query?.toLowerCase())
      );
    }

    // Category filter
    if (filters?.category) {
      filtered = filtered?.filter((bag) => bag?.category === filters?.category);
    }

    // Dietary preference filter
    if (filters?.dietaryPreference) {
      filtered = filtered?.filter((bag) =>
      bag?.dietaryOptions?.includes(filters?.dietaryPreference)
      );
    }

    // Distance filter
    if (filters?.maxDistance) {
      filtered = filtered?.filter((bag) =>
      bag?.walkingDistance <= parseInt(filters?.maxDistance)
      );
    }

    // Price range filter
    if (filters?.priceRange) {
      const [min, max] = filters?.priceRange?.includes('+') ?
      [parseInt(filters?.priceRange?.replace('+', '')), Infinity] :
      filters?.priceRange?.split('-')?.map((p) => parseInt(p));

      filtered = filtered?.filter((bag) =>
      bag?.discountedPrice >= min && bag?.discountedPrice <= max
      );
    }

    // Urgency filter
    if (filters?.urgency) {
      filtered = filtered?.filter((bag) => bag?.urgency === filters?.urgency);
    }

    setFilteredBags(filtered);
  };

  const handlePurchase = async (bagId, actionType = 'purchase') => {
    // Simulate purchase or donation
    await new Promise((resolve) => setTimeout(resolve, 1500));

    if (actionType === 'donate') {
      // Handle donation logic
      console.log(`Donated bag ${bagId} to students in need`);
      // You could show a success message or update UI state
    } else {
      // Handle regular purchase
      console.log(`Purchased bag ${bagId}`);
    }

    // Navigate to bag details or show success
    navigate('/surprise-bag-details', { state: { bagId, purchased: true, actionType } });
  };

  const handleQRScan = () => {
    console.log('QR Scanner activated');
    // Handle QR scan logic
  };

  const handleEmergencyContact = () => {
    console.log('Emergency contact activated');
    // Handle emergency contact logic
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background pt-16">
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-32 bg-muted rounded-lg" />
            <div className="h-48 bg-muted rounded-lg" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)]?.map((_, i) =>
              <div key={i} className="h-96 bg-muted rounded-lg" />
              )}
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background pt-16">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading font-bold text-3xl text-foreground mb-2">
            ¡Hola, Sofía! 👋
          </h1>
          <p className="text-muted-foreground">
            Descubre bolsas sorpresa cerca de ti y sigue rescatando comida.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Search and Filters */}
            <SearchFilterBar
              onSearch={handleSearch}
              onFilter={handleFilter}
              filters={activeFilters} />


            {/* View Mode Toggle */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-muted-foreground">
                  {filteredBags?.length} bolsas disponibles
                </span>
                {Object.keys(activeFilters)?.some((key) => activeFilters?.[key]) &&
                <span className="text-xs bg-primary text-primary-foreground px-2 py-1 rounded-full">
                    Filtrado
                  </span>
                }
              </div>
              
              <div className="flex items-center space-x-1">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  iconName="Grid3X3" />

                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  iconName="List" />

                <Button
                  variant={viewMode === 'map' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('map')}
                  iconName="Map" />

              </div>
            </div>

            {/* Surprise Bags Grid */}
            {filteredBags?.length > 0 ?
            <div className={`grid gap-6 ${
            viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`
            }>
                {filteredBags?.map((bag) =>
              <SurpriseBagCard
                key={bag?.id}
                bag={bag}
                onPurchase={handlePurchase} />

              )}
              </div> :

            <div className="text-center py-12">
                <Icon name="Search" size={64} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-lg text-foreground mb-2">
                  No se encontraron bolsas
                </h3>
                <p className="text-muted-foreground mb-4">
                  Intenta ajustar tus filtros o busca en una zona diferente.
                </p>
                <Button
                variant="outline"
                onClick={() => {
                  setActiveFilters({});
                  setSearchQuery('');
                  setFilteredBags(mockSurpriseBags);
                }}>

                  Limpiar filtros
                </Button>
              </div>
            }
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Wallet Balance */}
            <WalletBalance walletData={mockWalletData} />

            {/* Quick Actions */}
            <QuickActions
              onQRScan={handleQRScan}
              onEmergencyContact={handleEmergencyContact} />


            {/* Impact Metrics */}
            <ImpactMetrics userImpact={mockUserImpact} />
          </div>
        </div>
      </div>
    </div>);

};

export default ConsumerDashboard;